import Vue from 'vue';

import { debounce } from '@dui/helpers/util-funcs';
import { DEBOUNCE_TIME } from '@d-shared/config';
import { getErrorMessage } from '@d-shared/utils/errors';
import searchService from '@d-services/search-service/search-service';

const RESULT_TYPES = [
  {
    title: 'Genres',
    type: 'genre',
  },
  {
    title: 'Discovery Books',
    type: 'book',
  },
  {
    title: 'Books',
    type: 'external_book',
  },
  {
    title: 'People',
    type: 'user',
  },
];

const _debounceSearch = debounce(async function(commit, searchTerm) {
  try {
    const searchResults = await searchService.getSearchResults(searchTerm);
    commit('saveIsEmpty', (searchResults.meta.total_count === 0));
    commit('saveResults', searchResults.results);
  } catch (error) {
    Vue.duiNotificationManager.error(getErrorMessage(error));
  }

  commit('saveIsLoading', false);
}, DEBOUNCE_TIME.normal);

function _getGroupedResults(allResults) {
  const groupedResults = [];

  if (!allResults || !allResults.length) {
    return groupedResults;
  }

  RESULT_TYPES.forEach((resultType) => {
    const typeResults = allResults.filter((entry) => entry.type === resultType.type);
    if (!typeResults.length) {
      return;
    }

    groupedResults.push({
      entries: typeResults,
      title: resultType.title,
      type: resultType.type,
    });
  });

  return groupedResults;
};

const state = {
  groupedResults: [],
  isEmpty: false,
  isLoading: false,
  results: null,
  searchTerm: null,
};

const getters = {
  'get-empty-state': (state) => state.isEmpty,
  'get-grouped-results': (state) => state.groupedResults,
  'get-loading-state': (state) => state.isLoading,
  'get-results': (state) => state.results,
  'get-search-term': (state) => state.searchTerm,
};

const actions = {
  reset({ commit }) {
    commit('saveIsEmpty', false);
    commit('saveIsLoading', false);
    commit('saveSearchTerm', null);
    commit('saveResults', null);
  },

  async searchFor({ commit }, searchTerm) {
    commit('saveIsLoading', true);
    commit('saveSearchTerm', searchTerm);

    _debounceSearch(commit, searchTerm);
  },
};

const mutations = {
  saveIsEmpty(state, isEmpty) {
    if (state.isEmpty === isEmpty) {
      return;
    }

    Vue.set(state, 'isEmpty', isEmpty);
  },

  saveIsLoading(state, isLoading) {
    if (state.isLoading === isLoading) {
      return;
    }

    Vue.set(state, 'isLoading', isLoading);
  },

  saveResults(state, results) {
    Vue.set(state, 'results', results);

    const groupedResults = _getGroupedResults(results);
    Vue.set(state, 'groupedResults', groupedResults);
  },

  saveSearchTerm(state, searchTerm) {
    Vue.set(state, 'searchTerm', searchTerm);
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
